/**
 * contaminant-glossary.js — Enhancement layer only.
 * All content is server-side rendered in the Liquid template.
 * This script adds: search, filter, sort, accordion toggle,
 * view toggle (cards/table), URL state, back-to-top.
 * No DATA array. No innerHTML rendering of content.
 */
(function () {
  'use strict';

  const cardsGrid   = document.getElementById('cardsGrid');
  const tableWrap   = document.getElementById('tableWrap');
  const tableBody   = document.getElementById('tableBody');
  const searchInput = document.getElementById('searchInput');
  const sortSelect  = document.getElementById('sortSelect');
  const filterRow   = document.getElementById('filterRow');
  const resultsCount = document.getElementById('resultsCount');
  const emptyState  = document.getElementById('emptyState');
  const resetBtn    = document.getElementById('resetBtn');
  const backToTop   = document.getElementById('backToTop');

  if (!cardsGrid) return; // guard: section not present on page

  // ── Collect all SSR card elements ──────────────────────────────────────────
  const allCards = Array.from(cardsGrid.querySelectorAll('.card'));

  // ── State ──────────────────────────────────────────────────────────────────
  let currentFilter = 'all';
  let currentView   = 'cards';
  let searchTerm    = '';
  let currentSort   = 'severity';
  let debounceTimer;

  const SEVERITY_ORDER = { high: 0, moderate: 1, low: 2 };

  // ── Filter & sort ──────────────────────────────────────────────────────────
  function getVisible() {
    let items = allCards;

    // Category filter
    if (currentFilter !== 'all') {
      items = items.filter(c => c.dataset.category === currentFilter);
    }

    // Search
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      items = items.filter(c => (c.dataset.search || '').includes(q));
    }

    // Sort
    items = [...items].sort((a, b) => {
      if (currentSort === 'severity') {
        const diff = SEVERITY_ORDER[a.dataset.severity] - SEVERITY_ORDER[b.dataset.severity];
        return diff !== 0 ? diff : (a.dataset.name || '').localeCompare(b.dataset.name || '');
      }
      if (currentSort === 'name') return (a.dataset.name || '').localeCompare(b.dataset.name || '');
      if (currentSort === 'category') {
        const diff = (a.dataset.category || '').localeCompare(b.dataset.category || '');
        return diff !== 0 ? diff : (a.dataset.name || '').localeCompare(b.dataset.name || '');
      }
      return 0;
    });

    return items;
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  function render() {
    const visible = getVisible();
    const visibleIds = new Set(visible.map(c => c.id));

    // Show/hide cards in sorted order
    allCards.forEach(c => { c.style.display = 'none'; });
    visible.forEach((c, i) => {
      c.style.display = '';
      // Maintain sort order in DOM by appending in order
      cardsGrid.appendChild(c);
    });

    // Update table body visibility (table rows mirror card data)
    const allRows = Array.from(tableBody.querySelectorAll('tr'));
    allRows.forEach(r => { r.style.display = 'none'; });

    // Sort table rows to match card order
    visible.forEach(c => {
      const row = tableBody.querySelector(`tr[data-id="${c.id}"]`);
      if (row) {
        row.style.display = '';
        tableBody.appendChild(row);
      }
    });

    // Empty state
    const hasResults = visible.length > 0;
    emptyState.style.display = hasResults ? 'none' : 'block';

    // Results count
    resultsCount.textContent = visible.length === allCards.length
      ? `${allCards.length} contaminants`
      : `${visible.length} of ${allCards.length}`;

    // View toggle
    if (currentView === 'cards') {
      cardsGrid.style.display = 'flex';
      tableWrap.classList.remove('active');
    } else {
      cardsGrid.style.display = 'none';
      tableWrap.classList.add('active');
    }

    updateURL();
  }

  // ── Accordion ──────────────────────────────────────────────────────────────
  function setupAccordion() {
    cardsGrid.addEventListener('click', function (e) {
      const header = e.target.closest('.card-header');
      if (!header) return;
      const card = header.closest('.card');
      const expanded = card.getAttribute('aria-expanded') === 'true';
      card.setAttribute('aria-expanded', String(!expanded));
      header.setAttribute('aria-expanded', String(!expanded));
    });

    cardsGrid.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      const header = e.target.closest('.card-header');
      if (!header) return;
      e.preventDefault();
      const card = header.closest('.card');
      const expanded = card.getAttribute('aria-expanded') === 'true';
      card.setAttribute('aria-expanded', String(!expanded));
      header.setAttribute('aria-expanded', String(!expanded));
    });
  }

  // ── Table row → card link ──────────────────────────────────────────────────
  function setupTableRowClick() {
    tableBody.addEventListener('click', function (e) {
      const row = e.target.closest('tr');
      if (!row || !row.dataset.id) return;
      const card = document.getElementById(row.dataset.id);
      if (!card) return;
      // Switch to cards view
      currentView = 'cards';
      document.querySelectorAll('.view-btn').forEach(b => {
        const isCards = b.dataset.view === 'cards';
        b.classList.toggle('active', isCards);
        b.setAttribute('aria-pressed', String(isCards));
      });
      render();
      // Expand the card
      card.setAttribute('aria-expanded', 'true');
      const header = card.querySelector('.card-header');
      if (header) header.setAttribute('aria-expanded', 'true');
      // Scroll to card
      setTimeout(() => card.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
    });
  }

  // ── Events ─────────────────────────────────────────────────────────────────
  // Search
  searchInput.addEventListener('input', () => {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      searchTerm = searchInput.value.trim().toLowerCase();
      render();
    }, 200);
  });

  // Sort
  sortSelect.addEventListener('change', () => {
    currentSort = sortSelect.value;
    render();
  });

  // Filter pills
  filterRow.addEventListener('click', e => {
    const btn = e.target.closest('.filter-btn');
    if (!btn) return;
    filterRow.querySelectorAll('.filter-btn').forEach(b => {
      b.classList.remove('active');
      b.setAttribute('aria-pressed', 'false');
    });
    btn.classList.add('active');
    btn.setAttribute('aria-pressed', 'true');
    currentFilter = btn.dataset.filter;
    render();
  });

  // View toggle
  document.querySelectorAll('.view-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      document.querySelectorAll('.view-btn').forEach(b => {
        b.classList.remove('active');
        b.setAttribute('aria-pressed', 'false');
      });
      btn.classList.add('active');
      btn.setAttribute('aria-pressed', 'true');
      currentView = btn.dataset.view;
      render();
    });
  });

  // Table header sort
  document.querySelectorAll('.data-table th[data-sort]').forEach(th => {
    th.addEventListener('click', () => {
      const key = th.dataset.sort;
      sortSelect.value = key;
      currentSort = key;
      document.querySelectorAll('.data-table th').forEach(h => {
        h.classList.remove('sorted');
        h.setAttribute('aria-sort', 'none');
      });
      th.classList.add('sorted');
      th.setAttribute('aria-sort', 'ascending');
      render();
    });
  });

  // Reset
  resetBtn.addEventListener('click', () => {
    searchInput.value = '';
    searchTerm = '';
    currentFilter = 'all';
    currentSort = 'severity';
    sortSelect.value = 'severity';
    filterRow.querySelectorAll('.filter-btn').forEach(b => {
      b.classList.remove('active');
      b.setAttribute('aria-pressed', 'false');
    });
    const allBtn = filterRow.querySelector('[data-filter="all"]');
    if (allBtn) {
      allBtn.classList.add('active');
      allBtn.setAttribute('aria-pressed', 'true');
    }
    render();
  });

  // Back to top
  window.addEventListener('scroll', () => {
    backToTop.classList.toggle('visible', window.scrollY > 600);
  }, { passive: true });
  backToTop.addEventListener('click', () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  });

  // ── URL State ──────────────────────────────────────────────────────────────
  function updateURL() {
    const params = new URLSearchParams();
    if (searchTerm) params.set('q', searchTerm);
    if (currentFilter !== 'all') params.set('category', currentFilter);
    if (currentSort !== 'severity') params.set('sort', currentSort);
    if (currentView !== 'cards') params.set('view', currentView);
    const qs = params.toString();
    const url = qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
    history.replaceState(null, '', url);
  }

  function readURL() {
    const params = new URLSearchParams(window.location.search);
    if (params.has('q')) {
      searchTerm = params.get('q').toLowerCase();
      searchInput.value = params.get('q');
    }
    if (params.has('category')) {
      const cat = params.get('category');
      currentFilter = cat;
      filterRow.querySelectorAll('.filter-btn').forEach(b => {
        const isMatch = b.dataset.filter === cat;
        b.classList.toggle('active', isMatch);
        b.setAttribute('aria-pressed', String(isMatch));
      });
    }
    if (params.has('sort')) {
      const s = params.get('sort');
      if (['severity', 'name', 'category'].includes(s)) {
        currentSort = s;
        sortSelect.value = s;
      }
    }
    if (params.has('view') && params.get('view') === 'table') {
      currentView = 'table';
      document.querySelectorAll('.view-btn').forEach(b => {
        const isTable = b.dataset.view === 'table';
        b.classList.toggle('active', isTable);
        b.setAttribute('aria-pressed', String(isTable));
      });
    }
  }

  // ── Hash / anchor handling ─────────────────────────────────────────────────
  function handleHash() {
    const hash = window.location.hash.replace('#', '');
    if (!hash) return;
    const card = document.getElementById(hash);
    if (!card || !card.classList.contains('card')) return;
    // Ensure card is visible (clear any active filters)
    currentFilter = 'all';
    filterRow.querySelectorAll('.filter-btn').forEach(b => {
      const isAll = b.dataset.filter === 'all';
      b.classList.toggle('active', isAll);
      b.setAttribute('aria-pressed', String(isAll));
    });
    render();
    // Expand and scroll
    card.setAttribute('aria-expanded', 'true');
    const header = card.querySelector('.card-header');
    if (header) header.setAttribute('aria-expanded', 'true');
    setTimeout(() => card.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
  }

  // ── Init ───────────────────────────────────────────────────────────────────
  setupAccordion();
  setupTableRowClick();
  readURL();
  render();

  // Handle direct anchor links (e.g. /pages/contaminant-glossary#lead)
  if (window.location.hash) {
    handleHash();
  }
  window.addEventListener('hashchange', handleHash);

})();
